import styles from './attachments-list.module.scss';
import { useAttachmentsContext } from '../context/attachments-context';
import { AttachmentItem } from './attachment-item';
import { Button, Flex } from 'antd';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import type { CreateFileFormValuesApi } from '../lib/types';
import { uploadFile } from '@shared/utils/upload-file';
import { enhancedApi } from '@shared/api';
import { store, useAppDispatch, useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

interface AttachmentsListProps {
  onStateChange?: (state: { fileCount: number; isFormInvalid: boolean }) => void;
  isUploadButtonHidden?: boolean;
  uploadButtonLabel?: string;
  className?: string;
  variant?: 'default' | 'compact';
}

export interface AttachmentsListRef {
  uploadAll: () => Promise<void>;
}

export const AttachmentsList = forwardRef<AttachmentsListRef, AttachmentsListProps>(
  (
    {
      onStateChange,
      isUploadButtonHidden = false,
      uploadButtonLabel,
      className,
      variant = 'default',
    },
    ref,
  ) => {
    const { t } = useTranslation('translation');
    const organizationId = useAppSelector(authSelectors.selectCurrentOrganizationId);

    const { fileKeys, attachmentsRecord, removeAttachment } = useAttachmentsContext();
    const [formStateMap, setFormStateMap] = useState<
      Record<string, { values: CreateFileFormValuesApi; isValid: boolean }>
    >({});

    const [uploadProgressMap, setUploadProgressMap] = useState<
      Record<string, { percentage: number; error: boolean }>
    >({});

    const resolvedUploadLabel = uploadButtonLabel ?? t('common.actions.uploadAll');

    const isFormInvalid = useMemo(() => {
      return Object.values(formStateMap).some(({ isValid }) => {
        return !isValid;
      });
    }, [formStateMap]);

    const dispatch = useAppDispatch();

    useEffect(() => {
      onStateChange?.({ fileCount: fileKeys.length, isFormInvalid });
    }, [fileKeys.length, isFormInvalid, onStateChange]);

    const handleFormChange = (
      fileKey: string,
      partialValues: Partial<CreateFileFormValuesApi>,
      isValid: boolean,
    ) => {
      setFormStateMap((prev) => {
        const prevValues = prev[fileKey]?.values ?? {};
        return {
          ...prev,
          [fileKey]: {
            values: { ...prevValues, ...partialValues },
            isValid,
          },
        };
      });
    };

    const handleUploadAll = useCallback(async () => {
      await Promise.all(
        fileKeys.map(async (fileKey) => {
          const attachment = attachmentsRecord.get(fileKey);
          const formState = formStateMap[fileKey];
          if (!attachment || !formState) return;

          try {
            const result = await uploadFile({
              file: attachment.file,
              formValues: formState.values,
              organizationId: Number(organizationId),
              onUploadProgress: (progressPercent) =>
                setUploadProgressMap((prev) => ({
                  ...prev,
                  [fileKey]: {
                    ...prev[fileKey],
                    percentage: progressPercent,
                  },
                })),
            });

            const cachedArgsGetKnowledgeList = enhancedApi.util.selectCachedArgsForQuery(
              store.getState(),
              'getKnowledgeListKnowledgeListGet',
            );

            cachedArgsGetKnowledgeList?.forEach((args) => {
              dispatch(
                enhancedApi.util.updateQueryData(
                  'getKnowledgeListKnowledgeListGet',
                  args,
                  (draft) => {
                    draft.unshift(result);
                  },
                ),
              );
            });

            setUploadProgressMap((prev) => ({
              ...prev,
              [fileKey]: { percentage: 100, error: false },
            }));

            removeAttachment(fileKey, true, attachment.isDefault || false);
          } catch {
            setUploadProgressMap((prev) => ({
              ...prev,
              [fileKey]: { ...prev[fileKey], error: true },
            }));
          }
        }),
      );
    }, [attachmentsRecord, fileKeys, formStateMap, removeAttachment]);

    useImperativeHandle(
      ref,
      () => ({
        uploadAll: handleUploadAll,
      }),
      [handleUploadAll],
    );

    if (!fileKeys.length) return null;

    return (
      <Flex className={classNames(styles.scroll, className)} vertical>
        {fileKeys.map((fileKey) => {
          const attachment = attachmentsRecord.get(fileKey);
          if (!attachment) return null;

          return (
            <AttachmentItem
              key={fileKey}
              fileKey={fileKey}
              fileName={attachment.fileName}
              fileSize={attachment.size}
              isDefault={attachment.isDefault}
              abortController={attachment.abortController}
              uploadState={uploadProgressMap[fileKey]}
              onRemove={removeAttachment}
              onFormChange={handleFormChange}
              variant={variant}
            />
          );
        })}

        {!isUploadButtonHidden && (
          <Button
            type="primary"
            className={styles.button}
            onClick={handleUploadAll}
            disabled={isFormInvalid}
          >
            {resolvedUploadLabel}
          </Button>
        )}
      </Flex>
    );
  },
);
